import styled from "styled-components";
import { ChartCardPanel } from "src/view/statView/ChartCardPanel";
import { AnalysisRow } from "src/view/statView/OverviewAnalysisRows";
import type { ClientFinanceAccess } from "src/services/clientListRequest";
import { portfolioFilterSurface } from "./portfolioFilterStyle";

export const ClientFilters = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 12px;
	padding: 14px 16px;
	${portfolioFilterSurface}
	.search-field {
		flex: 1 1 240px;
		max-width: 440px;
		min-width: 180px;
	}
	.filter-field {
		flex: 0 1 160px;
		min-width: 140px;
	}
	.reset {
		margin-left: auto;
	}
	@media (max-width: 600px) {
		.search-field {
			flex-basis: 100%;
			max-width: none;
		}
		.filter-field {
			flex: 1 1 140px;
		}
	}
`;

export const ClientPanel = styled(ChartCardPanel)<{ $financeAccess: ClientFinanceAccess }>`
	container-type: inline-size;
	--client-columns: 52px minmax(220px, 1fr) minmax(160px, 0.35fr) 164px 115px 130px 30px;
	--client-min-width: 995px;
	.client-list-scroll {
		max-width: 100%;
		overflow-x: auto;
		border-radius: 11px 11px 0 0;
	}
	.client-list-scroll:focus-visible {
		outline: 2px solid #6374d9;
		outline-offset: -2px;
	}

	.page-size select {
		border: 1px solid #dce2eb;
		background: white;
		border-radius: 6px;
		color: #475467;
		padding: 7px 9px;
		font: inherit;
		cursor: pointer;
	}
	.icon-button {
		display: inline-flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		padding: 0;
		border: 0;
		border-radius: 6px;
		color: #667085;
		background: transparent;
		cursor: pointer;
	}
	.icon-button:hover:not(:disabled) {
		color: #167ca6;
		background: #edf8fd;
	}
	.icon-button:disabled {
		opacity: 0.45;
		cursor: default;
	}
	button:focus-visible,
	select:focus-visible {
		outline: 2px solid #6374d9;
		outline-offset: 2px;
	}
	.column-head {
		display: grid;
		grid-template-columns: var(--client-columns);
		min-width: var(--client-min-width);
		box-sizing: border-box;
		align-items: center;
		gap: 14px;
		padding: 9px 20px;
		border-radius: 11px 11px 0 0;
		background: #fafbfe;
		color: #667085;
		font-size: 13px;
	}
	.column-head > :nth-child(6) {
		text-align: right;
	}
	.column-head button {
		display: inline-flex;
		justify-content: flex-start;
		align-items: center;
		gap: 5px;
		border: 0;
		padding: 0;
		color: inherit;
		font: inherit;
		background: none;
		cursor: pointer;
	}
	.column-head > :first-child button {
		justify-content: flex-start;
	}
	.column-head button[data-selected="true"] {
		color: #167ca6;
	}
	.state {
		display: flex;
		min-height: 200px;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 24px;
		text-align: center;
		color: #667085;
		font-size: 13px;
		border-top: 1px solid #edf0f5;
	}
	.state strong {
		color: #344054;
		font-size: 14px;
		font-weight: 600;
	}
	.notice {
		margin: 0;
		padding: 10px 20px;
		border-top: 1px solid #edf0f5;
		background: #fffbf0;
		color: #946200;
		font-size: 13px;
		line-height: 1.5;
	}
	.portfolio-footer {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 12px 20px;
		padding: 12px 20px;
		border-top: 1px solid #edf0f5;
		font-size: 13px;
		color: #667085;
	}
	.page-size {
		margin-left: auto;
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.finance-note {
		margin: 0;
		padding: 0 20px 12px;
		color: #667085;
		font-size: 13px;
		line-height: 1.6;
	}
	@container (max-width: 540px) {
		.portfolio-footer {
			padding: 10px 16px;
		}
	}
`;

export const ClientRow = styled(AnalysisRow)`
	min-width: var(--client-min-width);
	&::before {
		display: none;
	}
	.client-grid {
		display: grid;
		grid-template-columns: var(--client-columns);
		align-items: center;
		gap: 14px;
		padding: 10px 20px;
		min-height: 62px;
		box-sizing: border-box;
		cursor: pointer;
	}
	.client-identity,
	.client-meta {
		min-width: 0;
	}
	.client-identity {
		position: relative;
	}
	.client-identity::before {
		content: "";
		position: absolute;
		left: -14px;
		top: 0;
		bottom: 0;
		width: 1px;
		background: #e1e6ef;
		pointer-events: none;
	}
	.client-name {
		border: 0;
		background: none;
		padding: 0;
		text-align: left;
		font: inherit;
		color: #344054;
		font-size: 14px;
		font-weight: 600;
		line-height: 1.5;
		cursor: pointer;
		overflow-wrap: anywhere;
	}
	.client-name:hover {
		color: #167ca6;
	}
	.client-payout {
		min-width: 0;
		color: #344054;
		font-size: 13px;
		line-height: 1.5;
	}
	.payout-empty {
		color: #667085;
	}
	.payout-badge {
		display: inline-block;
		padding: 2px 8px;
		border: 1px solid #d0d5dd;
		border-radius: 6px;
		background: #fff;
		color: #344054;
		font-size: 12px;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		line-height: 1.5;
	}
	.client-id {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px 10px;
		margin-top: 3px;
		color: #667085;
		font-size: 12px;
		line-height: 1.45;
		font-variant-numeric: tabular-nums;
	}
	.client-created {
		color: #344054;
		font-size: 13px;
		font-weight: 400;
		line-height: 1.5;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
	}
	.country {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		color: #667085;
	}
	.country .country-flag {
		display: block;
		width: 28px;
		height: 18px;
		flex: 0 0 28px;
		object-fit: cover;
		border-radius: 3px;
		box-shadow: 0 0 0 1px #b7c3cf;
	}
	.copy-id {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		border: 0;
		background: none;
		color: inherit;
		padding: 0;
		font: inherit;
		cursor: pointer;
	}
	.copy-id:hover {
		color: #167ca6;
	}
	.client-value {
		min-width: 0;
		text-align: right;
		font-size: 14px;
		font-weight: 400;
		line-height: 1.5;
		font-variant-numeric: tabular-nums;
	}
	.conversion {
		display: flex;
		justify-content: flex-start;
	}
	.conversion-number {
		font-size: 16px;
		font-weight: 600;
		white-space: nowrap;
	}
	.activity {
		font-size: 13px;
	}
	.activity[data-inactive="true"] {
		color: #946200;
	}
	.row-actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}
	&[data-expanded="true"] > .client-grid {
		background: #f8fafc;
	}
	.client-grid:hover,
	.client-grid:focus-within {
		background: #f8fafc;
	}
	.expanded-report {
		padding: 0 20px 16px;
		background: #f8fafc;
	}
`;
